/* eslint-disable import/no-unresolved */
import React, { useEffect, useState, useContext } from 'react';
import { useSelector, useStore } from 'react-redux';
import Helmet from 'react-helmet';
import 'semantic-ui-css/semantic.min.css';
import { useIntl, FormattedMessage, FormattedDate } from 'react-intl';
import { Item, Grid } from 'semantic-ui-react';

import ResetPasswordButton from 'app/components/ResetPasswordButton';
import LanguageDropdown from 'app/components/LanguageDropdown';
import ApiCalls from 'app/utils/apiCalls';
import EditableField from 'app/components/EditableField';
import PurchaseHistory from 'app/components/PurchaseHistory';
import { selectUsername } from 'app/redux/global/selectors';
import DropdownCountriesList from 'app/components/Dropdown/CountriesList';
import countries from 'app/constants/countries';
import loadSharedUserConfigFromAPI from 'app/redux/sharedUserConfig/loadUserConfigFromAPI';
import { SharedUserConfigurationAPIContext } from 'app/interfaces/SharedUserConfiguration';

// eslint-disable-next-line react/prop-types
const GridRow = ({ title, item, style }) => (
  <Grid.Row style={style}>
    <Grid.Column>{title}</Grid.Column>
    <Grid.Column>{item}</Grid.Column>
  </Grid.Row>
);

const renderGridRow = (title, item, style) => <GridRow title={title} item={item} style={style} />;

const getSaveInvoiceAddressFieldFunction = (fieldName) => (fieldValue) => {
  ApiCalls.setInvoiceAddressSingleField(fieldName, fieldValue);
};

function AccountPage() {
  const intl = useIntl();
  const store = useStore();
  const sharedUserConfigurationAPI = useContext(SharedUserConfigurationAPIContext);
  const username = useSelector(selectUsername);

  const [invoiceAddress, setInvoiceAddress] = useState({});

  useEffect(() => {
    ApiCalls.getInvoiceAddress().then((response) => setInvoiceAddress(response.data));
    loadSharedUserConfigFromAPI(store, sharedUserConfigurationAPI);
  }, [store, sharedUserConfigurationAPI]);

  const renderInvoiceAddressRow = (fieldName, paddingBottom) =>
    renderGridRow(
      intl.formatMessage({ id: `invoiceAddressModal.${fieldName}` }),
      <EditableField
        type="text"
        placeholder={intl.formatMessage({ id: `invoiceAddressModal.${fieldName}` })}
        text={invoiceAddress[fieldName]}
        onTextChange={getSaveInvoiceAddressFieldFunction(fieldName)}
      />,
      { paddingBottom }
    );

  const handleChangeCountryCode = (event, { value }) => {
    const country = countries.find(({ countryCode }) => countryCode === value);
    // @todo preferer faire uns eul appel pour éditer la paire countryCode et country
    // @use ApiCalls.updateUserDetails(data) quand se sera disponible
    ApiCalls.setInvoiceAddressSingleField('countryCode', country.countryCode);
    ApiCalls.setInvoiceAddressSingleField('country', country.name);
    setInvoiceAddress((prevInvoiceAddress) => ({
      ...prevInvoiceAddress,
      countryCode: country.countryCode,
      country: country.name,
    }));
  };

  const renderUpdateButton = () => {
    if (process.env.PLATFORM !== 'website') {
      // eslint-disable-next-line global-require
      const UpdateButton = require('../../components/UpdateButton').default;
      return <UpdateButton color="blue" />;
    }
    return null;
  };

  return (
    <div className="billing">
      <Helmet title={intl.formatMessage({ id: 'account.pageTitle' })} />
      <div className="ui container" style={{ paddingTop: '40px' }}>
        <Item.Group divided>
          <Item style={{ paddingTop: '20px' }}>
            <Item.Content>
              <Item.Header as="h3">
                <FormattedMessage id="account.my_account.header" />
              </Item.Header>
              <Item.Meta />
              <Item.Description>
                <Grid columns={2} style={{ marginTop: '9px' }}>
                  {renderGridRow(intl.formatMessage({ id: 'forms.email' }), username)}
                  {renderGridRow(
                    intl.formatMessage({ id: 'forms.password' }),
                    <ResetPasswordButton />
                  )}
                  {process.env.PLATFORM === 'electron' &&
                    renderGridRow(
                      intl.formatMessage({ id: 'general.softwareVersion' }),
                      <div>
                        {ApiCalls.version} (<FormattedDate value={ApiCalls.releaseDate} />){' '}
                        {renderUpdateButton()}
                      </div>
                    )}
                  {renderGridRow(
                    <FormattedMessage id="account.configuration.language" />,
                    <LanguageDropdown location="accountPage" />
                  )}
                </Grid>
              </Item.Description>
              <Item.Extra />
            </Item.Content>
          </Item>

          <Item style={{ paddingTop: '20px' }}>
            <Item.Content>
              <Item.Header as="h3">
                <FormattedMessage id="account.billing.header" />
              </Item.Header>
              <Item.Meta />
              <Item.Description>
                <Grid columns={2} style={{ marginTop: '9px' }}>
                  {renderInvoiceAddressRow('workplaceName', 0)}
                  {renderInvoiceAddressRow('address', 0)}
                  {renderInvoiceAddressRow('postCode', 0)}
                  {renderInvoiceAddressRow('city', 0)}
                  {/* {renderInvoiceAddressRow('country', 0)} */}
                  <Grid.Row>
                    <Grid.Column>
                      <FormattedMessage id="invoiceAddressModal.country" />
                    </Grid.Column>
                    <Grid.Column>
                      <DropdownCountriesList
                        value={invoiceAddress.countryCode}
                        onChange={handleChangeCountryCode}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  {renderInvoiceAddressRow('phoneNumber', 10)}
                </Grid>
              </Item.Description>
              <Item.Extra />
            </Item.Content>
          </Item>

          <Item style={{ paddingTop: '20px', marginBottom: '40px !important' }}>
            <Item.Content>
              <Item.Header as="h3">
                <FormattedMessage id="account.subscriptionsAndCredits.header" />
              </Item.Header>
              <Item.Meta />
              <Item.Description>
                <PurchaseHistory />
              </Item.Description>
              <Item.Extra />
            </Item.Content>
          </Item>
        </Item.Group>
      </div>
    </div>
  );
}

export default AccountPage;
